"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactWindow = require("react-window");
var _Checkbox = _interopRequireDefault(require("./Checkbox"));
var _Input = _interopRequireDefault(require("../Input/Input"));
var _Button = _interopRequireDefault(require("../Button/Button"));
var _Dropdown = _interopRequireDefault(require("../Dropdown/Dropdown"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useLabel = _interopRequireDefault(require("../../hooks/useLabel"));
var _diacritics = require("../../helpers/diacritics");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/**
 * The CheckboxMultiple component offers a convenient way to display multiple Checkbox components
 */
const CheckboxMultiple = props => {
  const {
    options,
    checked,
    id: controlledId,
    name: controlledName,
    inputLabel,
    inputSelectionLabel,
    inputAllSelectedLabel,
    searchLabel,
    selectButtonLabel,
    selectedLabel,
    clearButtonLabel,
    availableLabel,
    helper,
    inline,
    displayAsButton,
    checkboxesClassName,
    size,
    color,
    outline,
    required,
    clearable,
    disabled,
    placeholder,
    atLeast,
    atMost,
    onChange,
    onError,
    className,
    displaySearchBar,
    splitDisplay,
    customMessage,
    mountOnOpen
  } = props;
  const id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  const name = controlledName || id;
  const {
    label
  } = (0, _useLabel.default)(inputLabel, {
    ...props,
    id,
    required
  });
  const [stateChecked, setChecked] = _react.default.useState(checked || []);
  const [searchTerms, setSearchTerms] = _react.default.useState('');
  const [allOptions, setAllOptions] = _react.default.useState(options || []);
  const [filteredOptions, setFilteredOptions] = _react.default.useState(options || []);
  const [error, setError] = _react.default.useState(false);
  const searchInput = _react.default.useRef();
  const [errors, methods] = (0, _useFormElementValidation.default)(stateChecked, {
    required,
    atLeast,
    atMost,
    customMessage
  });
  _react.default.useEffect(() => {
    setAllOptions(options || []);
  }, [options]);
  _react.default.useEffect(() => {
    const newChecked = checked.filter(c => allOptions.map(o => o.id).includes(c));
    setChecked(newChecked || []);
  }, [checked, allOptions]);
  _react.default.useEffect(() => {
    setError(errors.length > 0);
    if (typeof onError === 'function') {
      onError({
        name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError, name]);
  _react.default.useEffect(() => {
    const newOptions = allOptions.filter(option => (0, _diacritics.stripDiacritics)(option.label.toLowerCase()).includes((0, _diacritics.stripDiacritics)(searchTerms.toLowerCase())));
    setFilteredOptions(newOptions);
  }, [allOptions, searchTerms]);
  const handleChange = (checkedCheckbox, ref, event) => {
    const {
      id: checkboxId
    } = event.target;
    let newStateChecked = stateChecked;
    if (checkedCheckbox) {
      if (atMost && stateChecked && stateChecked.length === atMost) {
        newStateChecked.shift();
      }
      newStateChecked.push(checkboxId);
    } else {
      newStateChecked = stateChecked.filter(value => value !== checkboxId);
    }
    setChecked(newStateChecked);
    methods.onChangeCheckboxGroup(newStateChecked);
    if (typeof onChange === 'function') {
      onChange(newStateChecked, name, event);
    }
  };
  const handleBlur = () => methods.onChangeCheckboxGroup(stateChecked);
  const handleSelectAll = () => {
    const newStateChecked = filteredOptions.filter(option => !option.disabled).map(option => option.id);
    const concatChecked = Array.from(new Set(stateChecked.concat(newStateChecked)));
    setChecked(concatChecked);
    methods.onChangeCheckboxGroup(concatChecked);
    if (typeof onChange === 'function') {
      onChange(concatChecked);
    }
  };
  const handleUnselectAll = () => {
    const newStateChecked = filteredOptions.filter(option => !option.disabled).map(option => option.id);
    const filterChecked = stateChecked.filter(checked => !newStateChecked.includes(checked));
    setChecked(filterChecked);
    methods.onChangeCheckboxGroup(filterChecked);
    if (typeof onChange === 'function') {
      onChange(filterChecked);
    }
  };
  const customDisplay = () => {
    if (stateChecked.length) {
      if (stateChecked.length === options.length && options.length > 1) {
        return inputAllSelectedLabel;
      }
      if (stateChecked.length > 1) {
        return stateChecked.length + "/" + options.length + " " + inputSelectionLabel;
      }
      const opt = options.find(option => String(option.id) === String(stateChecked[0]) || String(option.key) === String(stateChecked[0]));
      return opt ? "" + opt.label : '';
    }
    return '';
  };
  const getIsSelectAllDisabled = () => {
    if (atMost === 0 || atMost && options && atMost < options.length) {
      return true;
    }
    if (options && stateChecked) {
      return options.length === stateChecked.length;
    }
    return false;
  };
  const handleClick = (event, isOpen) => {
    if (!isOpen) {
      searchInput.current && searchInput.current.querySelector('input').focus();
    }
  };
  const classesCheckboxGroup = (0, _clsx.default)('uk-checkbox-group', (helper.length > 0 || error) && 'has-helper', error && 'has-error', inline && 'uk-checkbox-group--inline', splitDisplay && 'uk-checkbox-multiple--splitted', className);
  const classCheckboxGroupInput = (0, _clsx.default)(stateChecked && stateChecked.length > 0 ? 'value-selected' : 'no-value-selected', required && stateChecked && stateChecked.length < 1 && 'has-error');
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple"
  }, /*#__PURE__*/_react.default.createElement(_Dropdown.default, {
    tabIndex: "-1",
    withIcon: false,
    onClick: handleClick,
    mountOnOpen: mountOnOpen,
    disabled: disabled,
    trigger: /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_Input.default, {
      disabled: !options || !options.length || disabled,
      placeholder: placeholder,
      clearable: clearable,
      readOnly: true,
      tabIndex: "0",
      className: classCheckboxGroupInput,
      value: customDisplay(),
      label: label,
      onChange: handleUnselectAll,
      "aria-label": "Display multiple checkboxes",
      "aria-describedby": (name || id) + "-helper-text",
      id: id,
      icon: disabled ? undefined : 'dropdownIndicator'
    }))
  }, displaySearchBar && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple__search-bar",
    ref: searchInput
  }, /*#__PURE__*/_react.default.createElement(_Input.default, {
    className: "input-search",
    value: searchTerms,
    onChange: searchValue => setSearchTerms(searchValue),
    clearable: true,
    placeholder: searchLabel,
    icon: "search",
    "aria-label": "Search"
  })), !splitDisplay && /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-multiple__select-options"
  }, /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleSelectAll,
    displayAsBlock: true,
    className: "select-all",
    color: "secondary",
    label: selectButtonLabel,
    disabled: getIsSelectAllDisabled(),
    size: "small"
  }), /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleUnselectAll,
    displayAsBlock: true,
    className: "unselect-all",
    color: "secondary",
    label: clearButtonLabel,
    disabled: !stateChecked.length,
    size: "small"
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: classesCheckboxGroup + " checkbox-list-container"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-checkbox-group__checkboxes"
  }, splitDisplay ? /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("div", null, /*#__PURE__*/_react.default.createElement("strong", null, selectedLabel, " (", filteredOptions.filter(o => stateChecked.includes(o.id)).length, ")"), /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleSelectAll,
    displayAsBlock: true,
    className: "select-all",
    color: "secondary",
    label: selectButtonLabel,
    disabled: getIsSelectAllDisabled(),
    size: "small"
  }), /*#__PURE__*/_react.default.createElement(_reactWindow.FixedSizeList, {
    height: 180,
    itemCount: filteredOptions.filter(o => stateChecked.includes(o.id)).length,
    itemSize: 36,
    itemData: filteredOptions.filter(o => stateChecked.includes(o.id))
  }, props => {
    // Access the items array using the "data" prop:
    const item = props.data[props.index];
    return item ? /*#__PURE__*/_react.default.createElement("div", {
      style: props.style
    }, /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
      key: item.id || id + "_" + item.index,
      name: item.name || name + "_" + item.index,
      id: item.id || id + "_" + item.index,
      label: item.label,
      checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + item.index),
      disabled: item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: checkboxesClassName,
      displayAsButton: displayAsButton
    })) : undefined;
  })), /*#__PURE__*/_react.default.createElement("div", null, /*#__PURE__*/_react.default.createElement("strong", null, availableLabel, " (", filteredOptions.filter(o => !stateChecked.includes(o.id)).length, ")"), /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: handleUnselectAll,
    displayAsBlock: true,
    className: "unselect-all",
    color: "secondary",
    label: clearButtonLabel,
    disabled: !stateChecked.length,
    size: "small"
  }), /*#__PURE__*/_react.default.createElement(_reactWindow.FixedSizeList, {
    height: 180,
    itemCount: filteredOptions.filter(o => !stateChecked.includes(o.id)).length,
    itemSize: 36,
    itemData: filteredOptions.filter(o => !stateChecked.includes(o.id))
  }, props => {
    // Access the items array using the "data" prop:
    const item = props.data[props.index];
    return item ? /*#__PURE__*/_react.default.createElement("div", {
      style: props.style
    }, /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
      key: item.id || id + "_" + item.index,
      name: item.name || name + "_" + item.index,
      id: item.id || id + "_" + item.index,
      label: item.label,
      checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + item.index),
      disabled: item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: checkboxesClassName,
      displayAsButton: displayAsButton
    })) : undefined;
  }))) : /*#__PURE__*/_react.default.createElement(_reactWindow.FixedSizeList, {
    height: 220,
    itemCount: filteredOptions.length,
    itemSize: 32,
    itemData: filteredOptions
  }, props => {
    // Access the items array using the "data" prop:
    const item = props.data[props.index];
    return item ? /*#__PURE__*/_react.default.createElement("div", {
      style: props.style
    }, /*#__PURE__*/_react.default.createElement(_Checkbox.default, {
      key: item.id || id + "_" + item.index,
      name: item.name || name + "_" + item.index,
      id: item.id || id + "_" + item.index,
      label: item.label,
      checked: stateChecked.includes(item.id) || stateChecked.includes(id + "_" + item.index),
      disabled: item.disabled || false,
      onChange: handleChange,
      onBlur: handleBlur,
      color: color,
      size: size,
      outline: outline,
      value: item.value,
      className: checkboxesClassName,
      displayAsButton: displayAsButton
    })) : undefined;
  })), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, error ? errors : helper), atMost && atMost === 1 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper"
  }, "maximum 1 option"), atMost && atMost > 1 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper"
  }, "maximum " + atMost + " options"))));
};
CheckboxMultiple.propTypes = {
  /**
   * Array of Checkboxes props
   */
  options: _propTypes.default.arrayOf(_propTypes.default.shape),
  /**
   * Ids of the Checkboxes to set as selected
   */
  checked: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])),
  /**
   * Prefix for the Checkboxes id (unless it has been set in the options prop)
   */
  id: _propTypes.default.string,
  /**
   * Prefix for the Checkboxes name (unless it has been set in the options prop)
   */
  name: _propTypes.default.string,
  /**
   * Label of the CheckboxeGroup
   */
  inputLabel: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Label of the Search-bar
   */
  searchLabel: _propTypes.default.string,
  /**
   * Label of select all button
   */
  selectButtonLabel: _propTypes.default.string,
  /**
   * Label of clear button
   */
  clearButtonLabel: _propTypes.default.string,
  /**
   * Layout of the CheckboxeGroup
   */
  inline: _propTypes.default.bool,
  /**
   * Change the display of the CheckboxeGroup
   */
  displayAsButton: _propTypes.default.bool,
  /**
   * ClassName to apply to each Checkbox
   */
  checkboxesClassName: _propTypes.default.string,
  /**
   * PlaceHolder of the Checkboxes
   */
  placeholder: _propTypes.default.string,
  /**
   * Add a button to clear the input
   */
  clearable: _propTypes.default.bool,
  /**
   * Size of the Checkboxes
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),
  /**
   * Background-color of selected Checkboxes (or checkmark color if outline)
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'success', 'alert', 'warning', 'white']),
  /**
   * Invert styling colors of the Checkboxes
   */
  outline: _propTypes.default.bool,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Form-validation: set the CheckboxGroup as required
   */
  required: _propTypes.default.bool,
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the minimal number of Checkboxes that must be checked
   */
  atLeast: _propTypes.default.number,
  /**
   * Form-validation: set the maximal number of Checkboxes that can be checked
   */
  atMost: _propTypes.default.number,
  /**
   * Callback function onChange
   * Only handle when onClickClear (clearable: handleOnClear) beacause Input is in readOnly mode
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Display 2 columns of checkboxes (selected/available)
   */
  splitDisplay: _propTypes.default.bool,
  /**
   * Display a search input to allow filter the checkboxes
   */
  displaySearchBar: _propTypes.default.bool,
  /**
   * Add a custom message when custom validation is used
   */
  customMessage: _propTypes.default.object,
  /**
   * Label of the Selection Checkboxe
   */
  inputSelectionLabel: _propTypes.default.string,
  /**
   * Add a custom label message for all input selected
   */
  inputAllSelectedLabel: _propTypes.default.string,
  /**
   * Only mount the content when the Dropdown open
   */
  mountOnOpen: _propTypes.default.bool
};
CheckboxMultiple.defaultProps = {
  inline: false,
  clearable: true,
  displayAsButton: false,
  size: 'default',
  color: 'default',
  outline: false,
  helper: '',
  required: false,
  disabled: false,
  displaySearchBar: false,
  inputSelectionLabel: 'selection',
  inputAllSelectedLabel: 'All selected',
  selectedLabel: 'Selected',
  availableLabel: 'Available',
  mountOnOpen: false,
  splitDisplay: false
};
var _default = exports.default = CheckboxMultiple;